import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { CheckCircle2 } from 'lucide-react';
import DiffView from '../../components/DiffView';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useDeployStatus } from './useDeployStatus';
import { isDeployInProgress } from './useReleaseStatus';
import { DeployBadge, shortSha, timeAgo } from './ui';

/**
 * Compare a past version with the current page (text changes first, source diff on request), or
 * restore it as a new commit. The default restore puts back the old texts only, keeping today's
 * layout, shared header and footer, and scripts; restoring the whole file is the explicit choice.
 */
const VersionModal = ({ show, mode, page, entry, onClose, onRestored }) => {
  const ensureToken = useAuthToken();
  const [restoreType, setRestoreType] = useState('TEXT');
  const [version, setVersion] = useState(null);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [showSource, setShowSource] = useState(mode === 'restore');
  const [message, setMessage] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);
  const deploy = useDeployStatus(result?.deploy);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setVersion(null);
    setError(null);
    (async () => {
      try {
        await ensureToken();
        const response = await marketingSiteService.pageVersion(page.path, entry.sha, restoreType);
        if (!cancelled) setVersion(response.data);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, 'Could not load that version.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, page.path, entry.sha, restoreType, ensureToken]);

  const restore = async () => {
    setRestoring(true);
    setError(null);
    setConflict(false);
    try {
      await ensureToken();
      const response = await marketingSiteService.restore({
        path: page.path,
        currentSha: page.sha,
        ref: entry.sha,
        mode: restoreType,
        message: message.trim() || undefined,
      });
      setResult(response.data);
      onRestored?.(response.data);
    } catch (err) {
      setConflict(err?.response?.status === 409);
      setError(apiErrorMessage(err, 'Restoring failed.'));
    } finally {
      setRestoring(false);
    }
  };

  const restoreMode = mode === 'restore';
  const defaultMessage = `Restore ${page.url} to ${shortSha(entry.sha)}`;

  return (
    <Modal show={show} onHide={restoring ? undefined : onClose} size="lg" fullscreen="sm-down" backdrop={restoring ? 'static' : true}>
      <Modal.Header closeButton={!restoring}>
        <Modal.Title>
          {result ? 'Restored' : restoreMode ? `Restore ${page.url}` : `Compare with ${shortSha(entry.sha)}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <div>
            <p className="d-flex align-items-center gap-2">
              <CheckCircle2 size={18} className="text-success" />
              Committed{' '}
              <a href={result.commitUrl} target="_blank" rel="noopener noreferrer">
                <code>{shortSha(result.commitSha)}</code>
              </a>{' '}
              to the test site.
            </p>
            <div className="d-flex align-items-center gap-2">
              Test site deploy: <DeployBadge deploy={deploy} />
              {isDeployInProgress(deploy) && <Spinner animation="border" size="sm" />}
            </div>
          </div>
        ) : (
          <>
            <p className="mse-hint">
              Version <code>{shortSha(entry.sha)}</code> by {entry.authorName}, {timeAgo(entry.date)}: {entry.message.split('\n')[0]}
            </p>
            {restoreMode && (
              <div className="mb-3">
                <Form.Check
                  type="radio"
                  id="restore-text"
                  name="restore-mode"
                  label="Restore the text only (keep today's layout, shared header and footer, and scripts)"
                  checked={restoreType === 'TEXT'}
                  onChange={() => setRestoreType('TEXT')}
                  disabled={restoring}
                />
                <Form.Check
                  type="radio"
                  id="restore-full"
                  name="restore-mode"
                  label="Restore the whole file exactly as it was"
                  checked={restoreType === 'FULL'}
                  onChange={() => setRestoreType('FULL')}
                  disabled={restoring}
                />
              </div>
            )}
            {error && (
              <Alert variant={conflict ? 'warning' : 'danger'}>
                {error}
                {conflict && <div className="mt-1">Close this, reload the page and open its history again.</div>}
              </Alert>
            )}
            {!version && !error && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" /> Loading version
              </div>
            )}
            {version && (
              <>
                {version.sameAsCurrent && (
                  <Alert variant="secondary">
                    {restoreMode && restoreType === 'TEXT'
                      ? 'The text of this version is the same as today; only markup or scripts differ. Choose the whole file to restore those.'
                      : 'This version is the same as the current page.'}
                  </Alert>
                )}
                {restoreMode && restoreType === 'TEXT' && version.unplaced?.length > 0 && (
                  <Alert variant="warning">
                    {version.unplaced.length} text{version.unplaced.length === 1 ? '' : 's'} from this version no longer
                    {version.unplaced.length === 1 ? ' has' : ' have'} a place on the page and will not be restored:
                    <ul className="mb-0 mt-1">
                      {version.unplaced.slice(0, 8).map((u, i) => (
                        <li key={i}>
                          <code>{u.label}</code> {u.text.length > 120 ? `${u.text.slice(0, 119)}…` : u.text}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}
                <div className="mse-sublabel mb-1">Text changes (current page, then this version)</div>
                <DiffView patch={version.textDiff} maxHeight={260} />
                <button type="button" className="mse-link-button mt-2" onClick={() => setShowSource((v) => !v)}>
                  {showSource ? 'Hide' : 'Show'} source changes {restoreMode ? 'that will be committed' : ''}
                </button>
                {showSource && <DiffView patch={version.patch} maxHeight={260} />}
                {restoreMode && !version.sameAsCurrent && (
                  <Form.Group className="mt-3">
                    <Form.Label>Commit message</Form.Label>
                    <Form.Control
                      value={message}
                      placeholder={defaultMessage}
                      onChange={(e) => setMessage(e.target.value)}
                      disabled={restoring}
                    />
                  </Form.Group>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result || !restoreMode ? (
          <Button variant="primary" onClick={onClose}>
            {result ? 'Done' : 'Close'}
          </Button>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onClose} disabled={restoring}>
              Cancel
            </Button>
            <Button variant="primary" onClick={restore} disabled={restoring || !version || version.sameAsCurrent}>
              {restoring ? 'Restoring' : 'Restore to test site'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default VersionModal;
